import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Icon } from '../utils/icons';
import { useIsMobile } from '../utils/useIsMobile';

import { LICENSED_STATES, PENDING_LICENSE_STATES } from '../constants/marketExpansion';
import CoverageSearch from './CoverageSearch';
import CoverageMap from './CoverageMap';
// ─────────────────────────────────────────────────────────────────────────────
// The buyer-eligibility checker — ONE implementation, two shells.
//
// ⚠️ EXTRACTED RATHER THAN COPIED. DoorlyDesk needed the same feature the partner
// portal has. Copying ~300 lines would have duplicated the AFM matrix rendering
// AND its fallback constants, and two copies of underwriting guidance drift
// silently: a partner and a staff member would be shown different numbers for the
// same buyer, and nothing would flag it. The live values come from
// GET /api/uw-matrix (backend constants/afmMatrix.js) in both shells, and the
// tables render only once that payload has arrived.
//
// ⚠️ THE SHELL AND THE AUTH ARE THE ONLY DIFFERENCES, so they are the only props.
//   partner : DoorlyProLayout + RequirePartnerAuth, partner JWT,
//             POST /api/doorlypro/eligibility-chat
//   admin   : AdminLayout,                          admin JWT,
//             POST /api/admin/partners/eligibility-chat
// Both endpoints are the SAME exported handler (routes/doorlypro.js), each behind
// its own guard — see the mount in routes/admin.js.
// ─────────────────────────────────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ── AFM Program Data ─────────────────────────────────────────────────────────
// 🔑 AFM v2 (2026-10-21): NO FALLBACK TABLES. Every value comes from GET /api/uw-matrix (backend
// constants/afmMatrix.js). The old fallbacks published a 550 minimum, a $150K tier and townhomes —
// a stale copy is worse than a short loading state, and the figures are the ones an AE quotes.

// ── 🚨 THIS SAID FLORIDA WAS ELIGIBLE ───────────────────────────────────────
//
// { state: 'Florida', abbr: 'FL', eligible: true } — in the eligibility tool a
// PARTNER opens to decide whether a buyer is worth submitting, and which the
// recruiting drip promises on day one. Doorly holds a Texas licence; the
// Florida licence is pending, and taking the application is itself the licensed
// activity under Fla. Stat. Ch. 494.
//
// ⚠️ THE FOURTH SURFACE, AND THE ONE THE SWEEPS KEPT MISSING. It reads "Texas"
// and "Florida" as full words in an object literal, so the checks that looked
// for "TX + FL" or a ['TX','FL'] array walked straight past it. That is the
// argument for the inverted guard in backend/test/noGeographicLiterals: forbid
// geography by default and allow-list the exceptions, rather than enumerate the
// shapes somebody has thought of.
//
// 🔑 DERIVED, AND A PENDING LICENCE IS NAMED RATHER THAN DROPPED. A partner
// working Florida deals learns nothing from a list that simply lacks it.
const STATES = [
  ...LICENSED_STATES.map((state) => ({ state, eligible: true })),
  ...PENDING_LICENSE_STATES.map((state) => ({
    state, eligible: false, note: 'licensing in progress — not yet available',
  })),
  { state: 'All Other States', eligible: false },
];

// ⚠️ THE CANNED PROMPTS ARE SENT AS THE USER'S OWN MESSAGE, so "My buyer" on a
// staff screen would put a partner success manager in the referrer's shoes in
// the transcript itself — and the assistant would answer them as the referrer.
// Rewritten per audience for the same reason the headings are.
const QUICK_CHECKS_FOR = (staff) => {
  const B = staff ? 'A buyer' : 'My buyer';
  return [
    { q: `${B} has a 580 FICO and wants to buy a $300K home in Houston with 10% down. Are they eligible?`, label: '580 FICO, $300K, Houston' },
    { q: 'Can a self-employed borrower with bank statements qualify?', label: 'Self-employed / bank statements' },
    { q: `${B} is an ITIN borrower. What are the requirements?`, label: 'ITIN borrower' },
    { q: 'What is the minimum down payment and credit score?', label: 'Min down payment & FICO' },
  ];
};

function MatrixTable({ title, headers, rows, iconName }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Icon name={iconName} size={18} color="#3a6ad9" />
        <span style={{ fontSize: '14px', fontWeight: '800', color: '#111827' }}>{title}</span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {headers.map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '700', color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                {row.map((cell, j) => (
                  <td key={j} style={{ padding: '10px 14px', color: '#374151', fontWeight: j === 0 ? '600' : '400', whiteSpace: 'nowrap' }}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ⚠️ WRITTEN AS WHOLE-PHRASE TERNARIES, NOT FRAGMENT INTERPOLATION. Composing
// `Ask about {BUYER}` reads fine and made the literal "Ask about your buyer"
// vanish from source — which broke backend/knowledge/verifyAppGuideRoutes, whose
// job is to notice when a control a guide quotes is renamed or removed. The
// control was NOT renamed for partners; the string was merely assembled at
// runtime, and a guard that cannot tell those apart is right to complain. Both
// phrases now appear verbatim, so the scan works and the copy is greppable.
//
// ⚠️ audience CHANGES WHO "the buyer" BELONGS TO, AND NOTHING ELSE. A partner is
// asking about THEIR buyer; a partner success manager is asking about SOMEONE
// ELSE'S. "Check if your buyer is a fit" is wrong on a staff screen — it quietly
// tells the reader they are the referrer. The matrix, the endpoint and the
// guidance are identical, so this is a copy switch, not a second experience.
export default function EligibilityChecker({ token, chatEndpoint, audience = 'partner', licenceNote = null }) {
  const staff = audience === 'staff';
  const [coverageAnswer, setCoverageAnswer] = useState(null);
  const isMobile = useIsMobile();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  // Live eligibility matrix from the backend single source of truth, so these
  // tables stay in lockstep with the pricing engine. No fallback: until it lands, a loading line.
  const [matrix, setMatrix] = useState(null);
  useEffect(() => {
    let alive = true;
    // The borrower-published numbers only (GET /api/uw-matrix/public); the full guide is for authorized viewers.
    fetch(`${API_URL}/api/uw-matrix/public`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d) setMatrix(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const tierRows = (matrix?.tiers || []).map(t => [t.loan, t.ltv, t.dti, t.fico, t.reserves]);
  const downPaymentRows = matrix?.downPayment || [];
  const noScore = matrix?.noScore || null;
  // 🔑 THE KEY-REQUIREMENT TILES, DERIVED. Each figure is read off the payload rows the tables render.
  const scoredDown = downPaymentRows.filter((r) => r[0] !== 'No score').map((r) => parseFloat(String(r[2]))).filter(Number.isFinite);
  const downRange = scoredDown.length ? `${Math.min(...scoredDown)}–${Math.max(...scoredDown)}%` : '—';
  const lastTier = (matrix?.tiers || [])[(matrix?.tiers || []).length - 1];
  const loanRange = matrix ? `$${Math.round(Number(matrix.minLoan) / 1000)}K–${String((lastTier && lastTier.loan) || '').split('–').pop().trim()}` : '—';
  const propertyRows = matrix?.propertyTypes
    ? [...matrix.propertyTypes.eligible.map((t) => ({ type: t, eligible: true })),
       ...matrix.propertyTypes.ineligible.map((t) => ({ type: t, eligible: false }))]
    : [];
  // 🔑 HOW THE NO-SCORE BAND SITS AGAINST THE LOWEST SCORED ONE, read off the
  // same array the table renders. The scored bands come last-is-lowest, so the
  // final DOWN_PAYMENT_BANDS row is the one to compare — excluding the no-score
  // row itself, which is appended after them.
  const noScoreVsLowest = (() => {
    if (!noScore) return null;
    const scored = (downPaymentRows || []).filter((r) => r[0] !== 'No score');
    const lowest = scored[scored.length - 1];
    if (!lowest) return null;
    const lowestDown = parseFloat(String(lowest[2]));
    if (!Number.isFinite(lowestDown)) return null;
    if (noScore.minDownPct > lowestDown) return `tighter than the lowest scored band (${lowest[0]}, ${lowest[2]} down)`;
    if (noScore.minDownPct < lowestDown) return `more lenient than the lowest scored band (${lowest[0]}, ${lowest[2]} down)`;
    return `the same terms as the lowest scored band (${lowest[0]})`;
  })();

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const sendMessage = async (text) => {
    const userMsg = text || input.trim();
    if (!userMsg) return;
    const newMessages = [...messages, { role: 'user', content: userMsg }];
    setMessages(newMessages);
    setInput('');
    setLoading(true);
    try {
      const res = await axios.post(chatEndpoint, { messages: newMessages }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
    } catch (e) {
      const errMsg = e.response?.data?.error || 'Something went wrong. Please try again.';
      setMessages(prev => [...prev, { role: 'assistant', content: `Sorry — ${errMsg}` }]);
    }
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  return (
    <>
        {/* ⚠️ TOP PADDING ON THE CONTAINER, NOT A MARGIN ON THE <h1>. The
            heading's own margin would collapse against the layout above it and
            the gap would come and go depending on what wraps this component —
            DoorlyPro and DoorlyDesk use different shells. Padding on the
            container cannot collapse.

            Same 16/24 rhythm the header block below already uses. */}
        {/* ── 🚨 4px OF HORIZONTAL PADDING ON A PHONE ─────────────────────────
            Jesse, on mobile: "the formatting of the Check Eiligibility page —
            needs padding on both sides and optimized for mobile."

            It read `isMobile ? '16px 4px 0' : '24px 0 0'`. Four pixels is not a
            gutter — every card, table and chip below ran to within 4px of the
            screen edge, which is what made the page read as broken rather than
            dense.

            ⚠️ THE DESKTOP ZERO WAS NOT WRONG, IT WAS LOAD-BEARING ON WIDE
            SCREENS ONLY. maxWidth:960 with margin auto supplies the gutters past
            ~1000px — and supplies NONE at 980px, where the content touched both
            edges. So both arms get real padding and the centring still applies
            above the max width.

            🔑 AND THIS COMPONENT IS SHARED WITH THE PARTNER-FACING PAGE, which
            had the same 4px. One fix, both audiences. */}
        <div style={{
          maxWidth: 960, margin: '0 auto',
          padding: isMobile ? '16px 16px 0' : '24px 24px 0',
        }}>
          <div style={{ marginBottom: isMobile ? '16px' : '24px' }}>
            <h1 style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '800', color: '#111827', margin: '0 0 6px' }}>Check Buyer Eligibility</h1>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>{staff ? "Use the AI assistant to quickly check if a buyer is a fit for Doorly's program, or browse our underwriting guidelines below." : "Use the AI assistant to quickly check if your buyer is a fit for Doorly's program, or browse our underwriting guidelines below."}</p>
          </div>

          {/* ── AI CHAT ── */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '16px', overflow: 'hidden', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #f3f4f6', background: '#f9fafb', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, #3a6ad9, #2854c0)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#fff', fontWeight: '800' }}>D</div>
              <div>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>Doorly Eligibility Assistant</div>
                <div style={{ fontSize: '11px', color: '#9ca3af' }}>{staff ? "Describe the buyer's situation to check eligibility" : "Describe your buyer's situation to check eligibility"}</div>
              </div>
            </div>

            {/* Chat messages */}
            <div ref={chatContainerRef} style={{ minHeight: '200px', maxHeight: '400px', overflowY: 'auto', padding: '20px' }}>
              {messages.length === 0 && (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <div style={{ marginBottom: '12px' }}><Icon name="chat" size={32} color="#3a6ad9" /></div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>{staff ? 'Ask about a buyer' : 'Ask about your buyer'}</div>
                  <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '20px', lineHeight: '1.6' }}>
                    {/* 🔑 An early estimate, not a decision (Jesse, 2026-10-29: estimate-style copy everywhere). */}
                    {staff ? "Share the buyer's credit score, income, purchase price, location, and down payment — and I'll give you an early estimate of whether they fit the program." : "Share your buyer's credit score, income, purchase price, location, and down payment — and I'll give you an early estimate of whether they fit the program. It's not an approval."}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                    {QUICK_CHECKS_FOR(staff).map(qc => (
                      <button key={qc.label} onClick={() => sendMessage(qc.q)}
                        style={{ padding: '8px 14px', background: '#edf1fb', color: '#3a6ad9', border: '1px solid #c5d3f5', borderRadius: '20px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: isMobile ? 'normal' : 'nowrap', textAlign: 'center' }}>
                        {qc.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: '12px' }}>
                  <div style={{
                    maxWidth: '80%', padding: '12px 16px', borderRadius: '14px',
                    background: msg.role === 'user' ? '#3a6ad9' : '#f3f4f6',
                    color: msg.role === 'user' ? '#fff' : '#374151',
                    fontSize: '13px', lineHeight: '1.7', whiteSpace: 'pre-wrap',
                  }}>
                    {msg.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div style={{ display: 'flex', marginBottom: '12px' }}>
                  <div style={{ padding: '12px 16px', borderRadius: '14px', background: '#f3f4f6', fontSize: '13px', color: '#9ca3af' }}>
                    Checking eligibility...
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div style={{ padding: isMobile ? '10px 12px' : '14px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: '8px' }}>
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !loading && sendMessage()}
                placeholder={staff ? (isMobile ? 'Describe a buyer...' : "Describe the buyer's situation...") : (isMobile ? 'Describe your buyer...' : "Describe your buyer's situation...")}
                style={{ flex: 1, padding: isMobile ? '10px 12px' : '11px 16px', border: '1px solid #d1d5db', borderRadius: '10px', fontSize: '14px', outline: 'none', fontFamily: 'inherit', minWidth: 0 }}
              />
              <button onClick={() => sendMessage()} disabled={loading || !input.trim()}
                style={{ padding: isMobile ? '10px 14px' : '11px 20px', background: loading || !input.trim() ? '#e5e7eb' : '#3a6ad9', color: loading || !input.trim() ? '#9ca3af' : '#fff', border: 'none', borderRadius: '10px', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '14px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                Send
              </button>
            </div>
          </div>

          {/* ── AFM UNDERWRITING MATRICES ── */}
          <div style={{ marginBottom: '16px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: '800', color: '#111827', margin: '0 0 4px' }}>AFM Underwriting Guidelines</h2>
            <p style={{ fontSize: '13px', color: '#9ca3af', margin: '0 0 16px' }}>Quick reference for Doorly's Ability-First Mortgage program requirements.</p>
          </div>

          {!matrix && (
            <div data-testid="eligibility-matrix-loading" style={{ fontSize: '13px', color: '#6b7280', marginBottom: '24px' }}>Loading the program matrix…</div>
          )}
          {matrix && (
          <div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }}>
            {/* Loan Tier Matrix */}
            <MatrixTable
              iconName="chart"
              title="Loan Amount Tiers"
              headers={['Loan Amount', 'Max LTV', 'Max DTI', 'Min FICO', 'Reserves']}
              rows={tierRows}
            />

            {/* FICO-Based Down Payment */}
            {/* 🔑 THE "No score" ROW ARRIVES IN THIS ARRAY FROM THE BACKEND, not
                from a special case here — guidePayload() appends it so the UW
                Guide page and the investor portal gain it in the same edit. A
                table titled "by Credit Score" that omitted the borrower with no
                score told three audiences the path did not exist. */}
            <MatrixTable
              iconName="user"
              title="Down Payment by Credit Score"
              headers={['Credit Score', 'Max LTV', 'Min Down Payment']}
              rows={downPaymentRows}
            />

            {/* ── 🚨 NO CREDIT SCORE ────────────────────────────────────────
                The row above gives the number and says nothing about what earns
                it. That is the half AEs actually get asked: a no-score file is
                eligible BECAUSE of the alternative-credit requirement, and
                quoting 15% without it invites a file that cannot document any of
                this. Rendered from the same payload, so it cannot drift from the
                row above it. */}
            {noScore && (
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon name="shield" size={18} color="#3a6ad9" />
                  <span style={{ fontSize: '14px', fontWeight: '800', color: '#111827' }}>No Credit Score</span>
                  <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px', background: '#f4fee8', color: '#2d6a0a', border: '1px solid #d4f99a' }}>Eligible</span>
                </div>
                <div style={{ padding: '16px 18px', fontSize: '13px', color: '#374151', lineHeight: 1.6 }}>
                  {/* 🚨 THE COMPARISON IS DERIVED, NOT ASSERTED. This line read
                      "the same terms as the lowest scored band" for exactly as
                      long as that was true — the band tightened to 20%/80% days
                      later and the numbers above it updated from the payload
                      while the sentence beneath them did not. A hardcoded claim
                      ABOUT two numbers goes stale even when both numbers are
                      live. So it is computed from the same rows the table above
                      renders. */}
                  <div style={{ marginBottom: 10 }}>
                    A thin file or no file qualifies under the non-traditional credit standard at{' '}
                    <strong>{noScore.minDownPct}% down / {Math.round(noScore.maxLTV * 100)}% max LTV</strong>
                    {noScoreVsLowest && <>{' '}— {noScoreVsLowest}</>}.
                  </div>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}>What it takes</div>
                  <ul style={{ margin: '0 0 10px', paddingLeft: 18 }}>
                    <li>{noScore.minAlternativeTradelines} alternative tradelines with {(noScore.historyMonths || []).join('–')} months of history</li>
                    <li>{noScore.requirement}</li>
                    <li>Authorised-user accounts do not count</li>
                    {noScore.reserveOverlayApplies && (
                      <li>No credit score is a reserve-overlay trigger: +{matrix?.reserveOverlayMonths} months on top of the base by loan amount, and +{matrix?.reserveOverlayMonths} for each other trigger that applies</li>
                    )}
                  </ul>
                  {noScore.maxAutomatedLoan && (
                    <div style={{ fontSize: '12px', color: '#a16207', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px' }}>
                      ⚠️ Priced automatically up to <strong>${(noScore.maxAutomatedLoan / 1000).toFixed(0)}K</strong>.
                      The tiers above that each require a minimum credit score, which a no-score file cannot meet —
                      larger loans are a referral to an underwriter, <strong>not a decline</strong>.
                    </div>
                  )}
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
              {/* Property Types */}
              <MatrixTable
                iconName="home"
                title="Approved Property Types"
                headers={['Property Type', 'Eligible']}
                rows={propertyRows.map(p => [
                  p.type,
                  <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px', background: p.eligible ? '#f4fee8' : '#fff0f3', color: p.eligible ? '#2d6a0a' : '#ff082f', border: `1px solid ${p.eligible ? '#d4f99a' : '#ffc5ca'}` }}>{p.eligible ? 'Yes' : 'No'}</span>,
                ])}
              />

              {/* Eligible States */}
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon name="target" size={18} color="#3a6ad9" />
                  <span style={{ fontSize: '14px', fontWeight: '800', color: '#111827' }}>Eligible States</span>
                </div>
                <div style={{ padding: '16px 18px' }}>
                  {STATES.map(s => (
                    <div key={s.state} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f9fafb' }}>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                        {s.state}
                        {/* 🔑 A PENDING LICENCE SAYS SO. "Not Available" alone is
                            accurate and unhelpful — a partner working Florida
                            deals needs to know whether to wait. */}
                        {s.note && <span style={{ color: '#9ca3af', fontWeight: 500 }}> — {s.note}</span>}
                      </span>
                      <span style={{ fontSize: '12px', fontWeight: '700', padding: '3px 10px', borderRadius: '20px', background: s.eligible ? '#f4fee8' : '#fff0f3', color: s.eligible ? '#2d6a0a' : '#ff082f', border: `1px solid ${s.eligible ? '#d4f99a' : '#ffc5ca'}` }}>
                        {s.eligible ? '✓ Active' : '✗ Not Available'}
                      </span>
                    </div>
                  ))}
                  <div style={{ marginTop: '12px', fontSize: '12px', color: '#9ca3af', lineHeight: '1.6' }}>
                    Rural properties are not eligible — check a ZIP in the coverage search below.
                  </div>
                </div>
              </div>
            </div>

            {/* Key Requirements */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon name="document" size={18} color="#3a6ad9" />
                <span style={{ fontSize: '14px', fontWeight: '800', color: '#111827' }}>Key Requirements</span>
              </div>
              <div style={{ padding: isMobile ? '12px' : '16px 18px', display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(200px, 1fr))', gap: isMobile ? '10px' : '14px' }}>
                {[
                  // ⚠️ DERIVED, NOT TYPED. This tile said 560 while the tier table
                  // directly above it — correctly wired to /api/uw-matrix — said
                  // 550. Same page, two numbers, and the partner-facing one was
                  // the wrong one. Same class as the Program Matrix on
                  // /underwriting, which a credit facility found for us.
                  { label: 'Minimum Credit Score', value: String(matrix?.ficoFloor), sub: 'Lowest representative score' },
                  { label: 'Minimum Down Payment', value: downRange, sub: 'Varies by credit score' },
                  { label: 'Loan Amount', value: loanRange, sub: 'Primary residence only' },
                  { label: 'Loan Term', value: '30-Year Fixed', sub: 'Portfolio program' },
                  { label: 'Occupancy', value: 'Owner-Occupied', sub: 'Primary residence only' },
                  { label: 'Income Types', value: 'W-2, 1099, Self-Employed, ITIN', sub: 'Verifiable income required' },
                  { label: 'ITIN Max DTI', value: `${Math.round(Number(matrix?.itin?.maxDTI) * 100)}%`, sub: 'Regardless of loan tier' },
                  { label: 'Cash to Close', value: 'DP + ~3.5%', sub: 'Down payment + closing costs' },
                ].map(item => (
                  <div key={item.label} style={{ background: '#f9fafb', borderRadius: '10px', padding: '14px' }}>
                    <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>{item.label}</div>
                    <div style={{ fontSize: '16px', fontWeight: '800', color: '#111827', marginBottom: '2px' }}>{item.value}</div>
                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>{item.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          )}

          <div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }}>
            {/* ── COVERAGE ─────────────────────────────────────────────────
                🔑 LAST, AND INSIDE THE PAGE'S OWN CARD SHELL. It was bolted
                above this component in both page wrappers, which put it ahead
                of the eligibility answer a partner actually came for and left
                it formatted unlike everything around it.

                ⚠️ ONE MOUNT, NOT TWO. Living here means DoorlyPro and
                DoorlyDesk get it from the same place, and `staff` — which this
                component already derives from `audience` — supplies the detail
                switch with no new prop plumbing.

                ⚠️ SEARCH ABOVE MAP, deliberately. The search answers the
                question somebody has ("this buyer, this ZIP"); the map answers
                "where, roughly" and cannot be precise about a ZIP spanning two
                counties. */}
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Icon name="target" size={18} color="#3a6ad9" />
                <span style={{ fontSize: '14px', fontWeight: '800', color: '#111827' }}>Coverage</span>
              </div>
              <div style={{ padding: '16px 18px' }}>
                {/* 🔑 THE ANSWER IS LIFTED SO THE MAP CAN REACT TO IT. The
                    two were siblings with no shared state, so checking a ZIP
                    left the map showing the whole footprint and nothing about
                    the ZIP just checked. */}
                <CoverageSearch licenceNote={licenceNote} onAnswer={setCoverageAnswer} />
                <div style={{ marginTop: '16px' }}>
                  <CoverageMap detail={staff} token={token} highlight={coverageAnswer} />
                </div>
              </div>
            </div>
          </div>
        </div>
    </>
  );
}
